import { ArrowLeft, ExternalLink, X } from 'lucide-react';
import type { ReactNode } from 'react';
import type { LastSuccessfulApply, SecretMetadata } from '../api/types';
import { CopyButton, IconButton } from '../components/ui';
import { externalTriggerKind } from '../lib/externalTriggers';
import { formatDateTime, formatRelative } from '../lib/format';
import { strings } from '../strings';
import { NativeName, SourceChip, StatusBadgeView, ValueCellView } from './cells';
import type { RowStatus, ValueCell, VariableRow } from './model';
import { deliveryEntry, locationEntry, metadataWords } from './registry';

const s = strings.variables.details;
const NEBISYS_TAGS = [
  'managed-by',
  'nebisys-deployment',
  'nebisys-kind',
  'nebisys-used-by',
  'nebisys-on-change',
];

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="var-details-section">
      <h4 className="var-details-heading">{title}</h4>
      {children}
    </section>
  );
}

function Pair({ label, children }: { label: string; children: ReactNode }) {
  return (
    <>
      <dt className="text-fg-muted">{label}</dt>
      <dd className="min-w-0 break-words">{children}</dd>
    </>
  );
}

export function TagsList({ tags }: { tags: Record<string, string> }) {
  const entries = Object.entries(tags);
  if (entries.length === 0) return <p className="text-fg-muted">{s.noTags}</p>;
  const ours = entries.filter(([k]) => NEBISYS_TAGS.includes(k));
  const theirs = entries.filter(([k]) => !NEBISYS_TAGS.includes(k));
  const list = (items: [string, string][]) => (
    <ul className="space-y-0.5 font-mono text-[12px]">
      {items.map(([key, value]) => (
        <li key={key} className="break-all">
          {key}={value}
        </li>
      ))}
    </ul>
  );
  return (
    <div className="space-y-2">
      {ours.length > 0 && (
        <div>
          <p className="text-[11px] font-semibold text-fg-muted">{s.nebisysTags}</p>
          {list(ours)}
        </div>
      )}
      {theirs.length > 0 && (
        <div>
          <p className="text-[11px] font-semibold text-fg-muted">{s.userTags}</p>
          {list(theirs)}
        </div>
      )}
    </div>
  );
}

/**
 * The details pane (spec 6.10.8) of a variable row, or of a secret of the Secrets view (`row` null).
 */
export function DetailsPane({
  row,
  secret,
  cell,
  statuses,
  actions,
  deliveryAction,
  activity,
  lastApply,
  appliedVersion,
  referenceStatusCheckedAt,
  appStatus,
  declaration,
  narrow,
  fullscreen = false,
  onClose,
}: {
  row: VariableRow | null;
  secret: SecretMetadata | null;
  cell: ValueCell;
  statuses: readonly RowStatus[];
  /** The actions that resolve the statuses (Set value, Accept, Recover, …). */
  actions: ReactNode;
  /** Refresh references, in the Delivery section. */
  deliveryAction?: ReactNode;
  /** Recent activity of the secret (deployment scope). */
  activity?: ReactNode;
  lastApply: LastSuccessfulApply | null;
  appliedVersion: number | null;
  referenceStatusCheckedAt: string | null;
  /** Per-app platforms: the app's own reference status line (feature spec 15 19.3). */
  appStatus?: ReactNode;
  /** The secret's declaration in the open document (design scope). */
  declaration?: { description?: string; onChange?: string; generate?: { length: number } } | null;
  narrow: boolean;
  /** Phones: a full-screen sheet with Back, closed by Esc too (spec 6.10.16). */
  fullscreen?: boolean;
  onClose: () => void;
}) {
  const title = row?.displayName ?? secret?.name ?? '';
  const location = row?.location ?? secret?.location ?? null;
  const metadata = row?.metadata ?? secret;
  const app = row?.resourceId ?? null;
  const delivery = row?.delivery ?? null;
  const entry = delivery ? deliveryEntry(delivery) : null;
  const apps = [...new Set((metadata?.usedBy ?? []).map((u) => u.resourceId))];
  const description = metadata?.description ?? declaration?.description ?? null;
  const onChange = metadata?.onChange ?? declaration?.onChange ?? null;
  const generate = declaration?.generate ?? null;
  return (
    <aside
      aria-label={s.title(title)}
      className={`var-details ${narrow ? 'var-details-narrow' : ''} ${fullscreen ? 'var-details-fullscreen' : ''}`}
      data-testid={fullscreen ? 'variable-details-fullscreen' : undefined}
      onKeyDown={(event) => {
        // Esc hides the pane (spec 6.10.8; on phones: Back before the sheet itself closes).
        if (event.key === 'Escape' && !event.defaultPrevented) {
          event.preventDefault();
          event.stopPropagation();
          onClose();
        }
      }}
    >
      <div className="var-details-head">
        {narrow && (
          <IconButton label={s.back} onClick={onClose}>
            <ArrowLeft aria-hidden className="size-4" />
          </IconButton>
        )}
        <div className="min-w-0 flex-1">
          <h3 className="truncate font-mono text-[14px] font-semibold">{title}</h3>
          {row && (
            <p className="truncate text-[12px] text-fg-muted">
              {row.resourceId}
              {row.property ? ` · ${strings.variables.table.fromProperty(row.property)}` : ''}
            </p>
          )}
          {row?.externalTrigger && (
            <p className="truncate text-[12px] text-fg-muted" data-testid="details-external-trigger">
              {strings.externalTriggers.readBy(
                row.externalTrigger.name,
                externalTriggerKind(row.externalTrigger.kind).label,
              )}
            </p>
          )}
        </div>
        {!narrow && (
          <IconButton label={s.close} onClick={onClose}>
            <X aria-hidden className="size-4" />
          </IconButton>
        )}
      </div>
      <div className="var-details-body">
        {location && (
          <Section title={s.source}>
            <dl className="var-pairs">
              <Pair label={s.source}>
                <SourceChip location={location} kind={row?.kind ?? secret?.kind} />
              </Pair>
              {location.container && (
                <Pair label={s.container}>
                  <span className="font-mono">{location.container}</span>{' '}
                  {location.consoleUrl && (
                    <a
                      className="link inline-flex items-center gap-1"
                      href={location.consoleUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {s.consoleLink}
                      <ExternalLink aria-hidden className="size-3" />
                    </a>
                  )}
                </Pair>
              )}
              {location.name && (
                <Pair label={locationEntry(location).nameLabel ?? s.nameInSource}>
                  <span className="inline-flex items-center gap-1">
                    <NativeName
                      name={location.name}
                      prefix={location.secret ? location.container : null}
                      className="min-w-0"
                    />
                    <CopyButton value={location.name} label={strings.variables.actions.copyNameInSource} />
                  </span>
                </Pair>
              )}
              {location.nativeId && (
                <Pair label={s.nativeId}>
                  <span className="inline-flex items-center gap-1">
                    <span className="font-mono text-[12px] break-all">{location.nativeId}</span>
                    <CopyButton value={location.nativeId} label={`${strings.common.copy} ${s.nativeId}`} />
                  </span>
                </Pair>
              )}
              {location.account && <Pair label={s.account}>{location.account}</Pair>}
              {location.region && <Pair label={s.region}>{location.region}</Pair>}
            </dl>
            {/* Feature spec 15 19.6: case note, kept versions. */}
            {locationEntry(location).caseNote &&
              (row?.secretName ?? secret?.name) &&
              location.name &&
              !location.name.endsWith(row?.secretName ?? secret?.name ?? '') && (
                <p className="help mt-1">{locationEntry(location).caseNote}</p>
              )}
            {location.limits?.versionsKept && (
              <p className="help mt-1" data-testid="versions-kept">
                {strings.vaults.versionsKept(
                  location.limits.versionsKept,
                  location.limits.versionDestroyDelayDays ?? 0,
                )}
              </p>
            )}
          </Section>
        )}
        <Section title={s.currentValue}>
          <ValueCellView cell={cell} tabbable />
          {row && !row.secret && row.expression && (
            <p className="mt-1 text-[12px] text-fg-muted">
              {s.expression}: <code className="font-mono">{row.expression}</code>
            </p>
          )}
          {row && !row.secret && row.scope === 'deployment' && lastApply && (
            <p className="mt-1 text-[12px] text-fg-muted">
              {s.lastApplied(appliedVersion, formatRelative(lastApply.completedAt), null)}
            </p>
          )}
        </Section>
        {metadata?.currentVersion && (
          <Section title={s.version}>
            <p>
              {s.current}: <span className="font-mono">{metadata.currentVersion}</span>
              {metadata.updatedAt && (
                <span className="text-fg-muted"> · {formatDateTime(metadata.updatedAt)}</span>
              )}
            </p>
            {location?.rotation && (
              <p className="text-[12px]" data-testid="rotation-line">
                {locationEntry(location).rotationText?.(location.rotation).text}
              </p>
            )}
            {metadata.lastRecorded && (
              <p className="text-[12px] text-fg-muted">
                {s.lastWritten(
                  metadata.lastRecorded.versionId.slice(0, 12),
                  metadata.lastRecorded.writtenBy,
                  formatDateTime(metadata.lastRecorded.createdOn),
                  metadata.lastRecorded.accepted,
                )}
              </p>
            )}
          </Section>
        )}
        <Section title={s.status}>
          {statuses.length === 0 && row?.problems.length === 0 && (
            <p className="text-fg-muted">{s.noIssues}</p>
          )}
          <ul className="space-y-1.5">
            {statuses.map((status) => (
              <li key={status.id}>
                <StatusBadgeView status={status} />
                {status.help && <p className="mt-0.5 text-[12px] text-fg-muted">{status.help}</p>}
              </li>
            ))}
            {row?.problems.map((problem) => (
              <li key={problem} className="text-[12px] text-danger">
                {problem}
              </li>
            ))}
          </ul>
          {actions && <div className="mt-2 flex flex-wrap gap-1.5">{actions}</div>}
        </Section>
        {delivery && entry && app && (
          <Section title={s.delivery(app)}>
            <p>{entry.label(delivery.mode, delivery)}</p>
            <p className="mt-0.5 text-[12px] text-fg-muted">{entry.pickupText([app], delivery)}</p>
            {appStatus && (
              <p
                className="mt-1 flex flex-wrap items-center gap-1 text-[12px]"
                data-testid="delivery-app-status"
              >
                {appStatus}
              </p>
            )}
            {!appStatus && row?.referenceStatus && (
              <p className="mt-1 text-[12px]">
                {row.referenceStatus.status}
                {referenceStatusCheckedAt && (
                  <span className="text-fg-muted">
                    {' '}
                    · {s.lastResolution(formatRelative(referenceStatusCheckedAt))}
                  </span>
                )}
              </p>
            )}
            {deliveryAction && (
              <div className="mt-1.5">
                {deliveryAction}
                <p className="mt-0.5 text-[12px] text-fg-muted">{entry.refreshText(app)}</p>
              </div>
            )}
            {entry.readersText(app, delivery) && (
              <>
                <p className="mt-1 text-[12px]">{s.readByServer}</p>
                <p className="mt-0.5 text-[12px] text-fg-muted">{entry.readersText(app, delivery)}</p>
              </>
            )}
          </Section>
        )}
        {(description || onChange || generate) && (
          <Section title={s.declaration}>
            {description && <p>{description}</p>}
            <p className="mt-1 flex flex-wrap gap-1">
              {onChange && <span className="badge tone-neutral">{s.onChange(onChange)}</span>}
              {(generate || metadata?.generated) && (
                <span className="badge tone-info">
                  {generate ? s.generate(generate.length) : strings.variables.kinds.generated}
                </span>
              )}
            </p>
          </Section>
        )}
        {apps.length > 0 && (
          <Section title={s.usedBy}>
            <ul className="flex flex-wrap gap-1">
              {(metadata?.usedBy ?? []).map((use) => (
                <li key={`${use.resourceId}/${use.setting}`} className="badge tone-neutral font-mono">
                  {use.resourceId} · {use.setting}
                </li>
              ))}
            </ul>
          </Section>
        )}
        {metadata && Object.keys(metadata.tags).length > 0 && (
          <Section title={metadataWords(s.tags, metadata.location)}>
            <TagsList tags={metadata.tags} />
          </Section>
        )}
        {activity && <Section title={s.recentActivity}>{activity}</Section>}
      </div>
    </aside>
  );
}
